import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { setPixieNext } from '../utils/pixieDustNav';

/**
 * Custom hook for navigation with pixie dust animation
 * Uses canonical sessionStorage-based navigation storage (pixieDustNav utility)
 * 
 * This is the SINGLE SOURCE OF TRUTH for navigating to itinerary views with pixie dust.
 * All itinerary navigation should go through this hook to ensure consistency.
 */
const usePixieDustNavigation = () => {
  const navigate = useNavigate();

  /**
   * Navigate to itinerary view with pixie dust animation
   * @param {string} itineraryId - The ID of the itinerary to navigate to
   */
  const navigateToItinerary = useCallback((itineraryId) => {
    if (!itineraryId) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[usePixieDustNavigation] Missing itineraryId parameter');
      }
      return;
    }

    // Compute the destination path
    const nextPath = `/agent/itinerary/view/${itineraryId}`;
    
    // Store destination in canonical storage (sessionStorage with TTL)
    setPixieNext(nextPath);

    // Step 1: Fade out the current page smoothly
    const currentPage = document.querySelector('main') || document.body;
    if (currentPage) {
      currentPage.style.transition = 'opacity 0.6s ease-out';
      currentPage.style.opacity = '0';
    }
    
    // Step 2: Navigate to animation page after fade out completes
    // Do NOT pass itineraryId via state or query params
    // The animation page will read from pixieDustNav storage
    setTimeout(() => {
      navigate('/agent/pixie-dust-animation', { replace: false });
    }, 600); // Wait for fade out to complete
  }, [navigate]);

  /**
   * Navigate to itinerary view with pixie dust animation (unified style)
   * @param {string} itineraryId - The ID of the itinerary to navigate to
   */
  const navigateToUnifiedItinerary = useCallback((itineraryId) => {
    navigateToItinerary(itineraryId);
  }, [navigateToItinerary]);

  return {
    navigateToItinerary,
    navigateToUnifiedItinerary
  };
};

export default usePixieDustNavigation;
